import { useState, useEffect, useMemo, useRef, useCallback   } from 'react';
import { Navigate, Route, Routes, useLocation, Link  } from 'react-router-dom';
import Filters from './components/Filters';
import MapSection from './components/MapSection';
import ToggleButton from "./components/ToggleButton";
import ComplexList from './components/ComplexList';
import ShowFiltersButton from './components/ShowFiltersButton';
import CitySelector, { MoreCitiesMenu } from './components/CitySelector';
import Notifications from './components/Notifications';
import { fetchCitiesAvailability, fetchComplexes } from './API';
import './styles/styles.css';
import { filterComplexes } from './utils/filtersUtils';
import { defaultFilters } from './utils/defaultFilters';
import { io } from 'socket.io-client';
import AdminLoginPage from './pages/AdminLoginPage';
import { getAdminToken } from "./admin/auth";
import { fetchAdminMe } from "./admin/api";
import AdminAuthButton from './components/AdminAuthButton';
import AdminComplexesPage from './pages/AdminComplexesPage';
import ThemeToggle from './components/ThemeToggle';
import AppProviders from './providers/AppProviders';
import { useCities } from './providers/CitiesContext';

function PublicApp( {isAdmin, setIsAdmin, theme, toggleTheme} ) {
  const { cities, isCitiesLoading } = useCities();
  const [selectedComplexId, setSelectedComplexId] = useState(null);
  const [selectedCity, setSelectedCity] = useState("");
  const [complexes, setComplexes] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [filters, setFilters] = useState(defaultFilters);
  const [isLoading, setIsLoading] = useState(false);
  const [showInactiveOnMap, setShowInactiveOnMap] = useState(false);
  const [cityAvailability, setCityAvailability] = useState({});

  useEffect(() => {
    if (isCitiesLoading || cities.length === 0) return;
    if (cities.some((city) => city.key === selectedCity)) return;
    setSelectedCity((cities.find((city) => city.key === "moscow") || cities[0]).key);
  }, [cities, isCitiesLoading, selectedCity]);

  const reqIdRef = useRef(0);
  const availabilityReqIdRef = useRef(0);

  const lastSyncAtRef = useRef(0);
  const lastVisibilitySyncAtRef = useRef(0);

  const loadComplexes = useCallback(async (city = selectedCity, { silent = false } = {}) => {
    if (!city) return;

    const rid = ++reqIdRef.current;

    if (!silent) {
      setIsLoading(true);
    }

    try {
      const data = await fetchComplexes(city);

      if (rid !== reqIdRef.current) return;

      setComplexes(Array.isArray(data) ? data : []);
      lastSyncAtRef.current = Date.now();
    } catch (e) {
      if (rid === reqIdRef.current) {
        console.error("Ошибка загрузки ЖК:", e);
      }
    } finally {
      if (rid === reqIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [selectedCity]);

  const checkCitiesAvailability = useCallback(async () => {
    const rid = ++availabilityReqIdRef.current;

    try {
      const data = await fetchCitiesAvailability();

      if (rid !== availabilityReqIdRef.current) return;

      setCityAvailability(data);
    } catch {
      // Keep previous selector colors; never fall back to loading all city complexes.
    }
  }, []);

  const handleFiltersApplied = (next) => {
    setFilters(next);
    setSelectedComplexId(null);
  };

  const handleFiltersReset = () => {
    setFilters((prev) => ({
      ...defaultFilters,
      showVehicles: prev.showVehicles,
      onlyApartments: prev.onlyApartments,
      onlyCommercial: prev.onlyCommercial,
      excludePik: prev.excludePik,
      excludeGranel: prev.excludeGranel,
    }));
    setSelectedComplexId(null);
  };

  const [notes, setNotes] = useState([]);
  const pushNote = (n) => {
    const id = (crypto?.randomUUID?.() ?? ('id_' + Date.now() + '_' + Math.random().toString(16).slice(2)));
    const note = { id, ...n };
    setNotes((prev) => [note, ...prev].slice(0, 10));
  };

  const [conn, setConn] = useState('offline');
  const socketRef = useRef(null);

  useEffect(() => {
    loadComplexes(selectedCity, { silent: false });
    checkCitiesAvailability();
  }, [selectedCity, loadComplexes, checkCitiesAvailability]);

  useEffect(() => {
    if (!selectedCity) return undefined;
    const API_BASE = process.env.REACT_APP_API_BASE;

    if (socketRef.current) {
      try { socketRef.current.close(); } catch {}
      socketRef.current = null;
    }

    const socket = io(API_BASE);
    socketRef.current = socket;

    socket.on('connect', () => {
      setConn('live');
      if (selectedCity) {
        socket.emit('subscribe', { city: selectedCity });
      }

      loadComplexes(selectedCity, { silent: true });
    });

    socket.on('disconnect', () => setConn('offline'));
    socket.io.on('reconnect_attempt', () => setConn('reconnecting'));
    socket.io.on('reconnect', () => setConn('live'));
    socket.io.on('error', () => setConn('reconnecting'));
    socket.on('connect_error', () => setConn('reconnecting'));

    const applyChange = (prev, change) => {
      const { operationType, fullDocument, documentKey, updateDescription } = change || {};
      const id = fullDocument?._id || documentKey?._id;
      if (!id) return prev;

      if (operationType === 'insert' || operationType === 'replace') {
        const i = prev.findIndex((x) => x._id === id);
        if (i >= 0) {
          const next = prev.slice();
          next[i] = fullDocument;
          return next;
        }
        return [fullDocument, ...prev];
      }
      if (operationType === 'update') {
        const exists = prev.find((x) => x._id === id);
        const merged = fullDocument || (exists ? { ...exists, ...(updateDescription?.updatedFields || {}) } : null);
        return merged ? prev.map((x) => x._id === id ? merged : x) : prev;
      }
      if (operationType === 'delete') {
        return prev.filter((x) => x._id !== id);
      }
      return prev;
    };

    const onChange = (change) => {
      setComplexes((prev) => applyChange(prev, change));
      checkCitiesAvailability();
      const op = change?.operationType;
      const doc = change.fullDocument || {};
      const id = doc?._id || change.documentKey?._id;

      if (op === 'update') {
        const uf = change.updateDescription?.updatedFields || {};
        if (Object.prototype.hasOwnProperty.call(uf, 'status')) {
          pushNote({ kind: 'enabled', complexId: id, name: doc?.name, city: doc?.city, enabled: !!uf.status });
        }
      }
    };

    const onEnabledEvt = (evt) => {
      setComplexes((prev) =>
        prev.map((x) =>
          x._id === evt.complexId
            ? { ...x, enabled: evt.enabled, status: evt.enabled }
            : x
        )
      );
      checkCitiesAvailability();
      pushNote({ kind: 'enabled', complexId: evt.complexId, name: evt.name, city: evt.city, enabled: evt.enabled });
    };

    socket.on('complexes:change', onChange);
    socket.on('complexes:enabled', onEnabledEvt);

    return () => {
      socket.off('complexes:change', onChange);
      socket.off('complexes:enabled', onEnabledEvt);
      try { socket.close(); } catch {}
      socketRef.current = null;
    };
  }, [selectedCity, loadComplexes, checkCitiesAvailability]);

    useEffect(() => {
    const RESYNC_COOLDOWN_MS = 1500;

    const resyncAfterTabReturn = () => {
      if (document.visibilityState !== 'visible') return;

      const now = Date.now();

      // защита от двойного вызова: visibilitychange + focus могут сработать почти вместе
      if (now - lastVisibilitySyncAtRef.current < RESYNC_COOLDOWN_MS) return;
      lastVisibilitySyncAtRef.current = now;

      const socket = socketRef.current;

      if (socket) {
        if (!socket.connected) {
          socket.connect();
        }

        if (selectedCity) {
          socket.emit('subscribe', { city: selectedCity });
        }
      }

      loadComplexes(selectedCity, { silent: true });
      checkCitiesAvailability();
    };

    document.addEventListener('visibilitychange', resyncAfterTabReturn);
    window.addEventListener('focus', resyncAfterTabReturn);
    window.addEventListener('pageshow', resyncAfterTabReturn);

    return () => {
      document.removeEventListener('visibilitychange', resyncAfterTabReturn);
      window.removeEventListener('focus', resyncAfterTabReturn);
      window.removeEventListener('pageshow', resyncAfterTabReturn);
    };
  }, [selectedCity, loadComplexes, checkCitiesAvailability]);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const filteredComplexes = useMemo(() => {
    const byCity = complexes.filter((x) => x.city === selectedCity);
    return filterComplexes(byCity, filters, selectedCity);
  }, [complexes, filters, selectedCity]);

  const mapFilters = useMemo(
    () => (isAdmin && showInactiveOnMap ? { ...filters, status: "" } : filters),
    [filters, isAdmin, showInactiveOnMap]
  );

  const mapComplexes = useMemo(() => {
    const byCity = complexes.filter((x) => x.city === selectedCity);
    return filterComplexes(byCity, mapFilters, selectedCity);
  }, [complexes, mapFilters, selectedCity]);

  useEffect(() => {
    if (!isAdmin && showInactiveOnMap) {
      setShowInactiveOnMap(false);
    }
  }, [isAdmin, showInactiveOnMap]);

  const firstRun = useRef(true);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setFilters(defaultFilters);
  }, [selectedCity]);

  return (
    <div className='app-layout'>
      <Notifications
        items={notes}
        onClose={(id) => setNotes((p) => p.filter((x) => x.id !== id))}
        onOpen={(complexId) => setSelectedComplexId(complexId)}
        
      />
      <div className='city-menu'>
        <CitySelector
          selectedCity={selectedCity}
          setSelectedCity={setSelectedCity}
          cityAvailability={cityAvailability}
        />
        <div className="city-menu-right">
        <MoreCitiesMenu
          selectedCity={selectedCity}
          setSelectedCity={setSelectedCity}
          cityAvailability={cityAvailability}
        />
        {isAdmin && (
          <Link
            to="/admin/complexes"
            className="admin-database-link"
            style={{
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              background: "var(--accent)",
              color: "var(--button-text)",
              height: "32px",
              padding: "0 10px",
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "13px",
              lineHeight: "32px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            Админка
          </Link>
        )}
        {isAdmin && (
          <label className="admin-inactive-toggle">
            <input
              type="checkbox"
              checked={showInactiveOnMap}
              onChange={(event) => {
                setShowInactiveOnMap(event.target.checked);
                setSelectedComplexId(null);
              }}
            />
            <span>Все ЖК</span>
          </label>
        )}
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <AdminAuthButton
          isAdmin={isAdmin}
          onLogout={() => setIsAdmin(false)}
        />
        </div>
      </div>
      <div className='main-content'>
        <ToggleButton
          sidebarOpen={sidebarOpen}
          onToggle={toggleSidebar}
          style={{ left: (sidebarOpen ? 419 + 10 : 10) }}
        />
        {sidebarOpen && (
          <ComplexList
            key={selectedCity}
            sidebarOpen={sidebarOpen}
            selectedComplexId={selectedComplexId}
            selectedCity={selectedCity}
            complexes={isAdmin && showInactiveOnMap ? mapComplexes : filteredComplexes}
            isLoading={isLoading}
            isAdmin={isAdmin}
            onLogout={() => setIsAdmin(false)}
          />
        )}
        <MapSection
          sidebarOpen={sidebarOpen}
          filters={filters}
          setSelectedComplexId={setSelectedComplexId}
          selectedCity={selectedCity}
          setSelectedCity={setSelectedCity}
          complexes={mapComplexes}
          conn={conn}
          
        />
        {filtersOpen ? (
          <Filters
            onClose={() => setFiltersOpen(false)}
            filters={filters}
            defaultFilters={defaultFilters}
            setFilters={setFilters}
            onApplied={handleFiltersApplied}
            onReset={handleFiltersReset}
          />
        ) : (
          <ShowFiltersButton onClick={() => setFiltersOpen(true)} />
        )}
      </div>
    </div>
  );
}

function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loadingAdmin, setLoadingAdmin] = useState(true);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme === "dark" || savedTheme === "light" ? savedTheme : "light";
  });
  const location = useLocation();

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    let active = true;

    async function checkAdmin() {
      const token = getAdminToken();

      if (!token) {
        if (active) {
          setIsAdmin(false);
          setLoadingAdmin(false);
        }
        return;
      }

      try {
        await fetchAdminMe();
        if (active) setIsAdmin(true);
      } catch {
        if (active) setIsAdmin(false);
      } finally {
        if (active) setLoadingAdmin(false);
      }
    }

    checkAdmin();

    return () => {
      active = false;
    };
  }, [location.pathname]);

  if (loadingAdmin) {
    return (
      <div style={{ padding: 24, color: "var(--text-main)" }}>
        Checking admin session...
      </div>
    );
  }

  return (
    <AppProviders>
      <Routes>
        <Route
          path="/"
          element={
            <PublicApp
              isAdmin={isAdmin}
              setIsAdmin={setIsAdmin}
              theme={theme}
              toggleTheme={toggleTheme}
            />
          }
        />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route
          path="/admin/complexes"
          element={<AdminComplexesPage isAdmin={isAdmin} theme={theme} toggleTheme={toggleTheme} />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppProviders>
  );
}

export default App;
